import React, { useState } from "react";
import { Button, FormControl, InputLabel, MenuItem, Select, Box } from "@mui/material";
import { ClipFormContainer } from "./Form.elements";
import { SearchFormWrapper, SearchActionsRow } from "../PackagesSection/SearchForm.elements";
import SearchTextField from "../Search/SearchTextField";
import MuiClipDateRangePicker from "@/components/Clips/MuiClipDateRangePicker";
import countries from "@/components/Util/countries.json";
import sortCountriesByName from "@/utils/sortCountries";

const regionOptions = [
    { label: "All Regions", value: "" },
    { label: "Americas", value: "AMERICAS" },
    { label: "EMEA", value: "EMEA" },
    { label: "APAC", value: "APAC" },
];

const initParams = {
    country: "",
    region: "",
    searchTerm: "",
    clipTypes: [],
    startDate: undefined,
    endDate: undefined,
};

const SearchPanel = ({
                         setSearchParams,
                         hasCountryField = false,
                         hasRegionField = false,
                         searchLabel = "Search",
                         searchPlaceholder = "Enter search term",
                     }) => {
    const [localSearchParams, setLocalSearchParams] = useState(initParams);
    const sortedCountries = React.useMemo(() => sortCountriesByName(countries), []);

    const updateLocalSearchParams = (v) => setLocalSearchParams((s) => ({ ...s, ...v }));

    const onSearch = () => {
        setSearchParams({
            ...localSearchParams,
            clipTypes: [...localSearchParams.clipTypes],
        });
    };

    const onResetFilters = () => {
        const resetValue = { ...initParams, clipTypes: [] };
        setSearchParams(resetValue);
        setLocalSearchParams(resetValue);
    };

    return (
        <ClipFormContainer>
            <SearchFormWrapper>
                <Box sx={{ minWidth: 0 }}>
                    <SearchTextField
                        value={localSearchParams.searchTerm}
                        onChange={(e) => updateLocalSearchParams({ searchTerm: e.target.value })}
                        label={searchLabel}
                        placeholder={searchPlaceholder}
                    />
                </Box>

                {hasCountryField && (
                    <FormControl sx={{ minWidth: 0 }}>
                        <InputLabel id="country-select-label">Country</InputLabel>
                        <Select
                            labelId="country-select-label"
                            label="Country"
                            value={localSearchParams.country}
                            onChange={(e) => updateLocalSearchParams({ country: e.target.value })}
                        >
                            <MenuItem key="empty" value="">
                                Select Country
                            </MenuItem>
                            {sortedCountries.map((country) => (
                                <MenuItem key={country.code} value={country.code}>
                                    {country.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {hasRegionField && (
                    <FormControl sx={{ minWidth: 0 }}>
                        <InputLabel id="region-select-label">Region</InputLabel>
                        <Select
                            labelId="region-select-label"
                            label="Region"
                            value={localSearchParams.region}
                            onChange={(e) => updateLocalSearchParams({ region: e.target.value })}
                        >
                            {regionOptions.map((region) => (
                                <MenuItem key={region.label} value={region.value}>
                                    {region.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <Box sx={{ minWidth: 0 }}>
                    <MuiClipDateRangePicker
                        label="Start Date"
                        value={localSearchParams.startDate}
                        onChange={(startDate) => updateLocalSearchParams({ startDate })}
                        format="yyyy/MM/dd"
                        slotProps={{
                            textField: {
                                placeholder: "YYYY/MM/DD",
                                fullWidth: true,
                            },
                        }}
                    />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <MuiClipDateRangePicker
                        label="End Date"
                        value={localSearchParams.endDate}
                        onChange={(endDate) => updateLocalSearchParams({ endDate })}
                        format="yyyy/MM/dd"
                        slotProps={{
                            textField: {
                                placeholder: "YYYY/MM/DD",
                                fullWidth: true,
                            },
                        }}
                    />
                </Box>

                <SearchActionsRow>
                    <Button
                        onClick={onSearch}
                        variant="contained"
                        sx={{
                            borderColor: "#40A31E",
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "18px 38px",
                            textTransform: "none",
                            minWidth: "160px",
                            "&:hover": {
                                backgroundColor: "#8095B7",
                            },
                        }}
                    >
                        Search
                    </Button>

                    <Button
                        onClick={onResetFilters}
                        variant="outlined"
                        sx={{
                            borderColor: "#707070",
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "18px 38px",
                            textTransform: "none",
                            minWidth: "160px",
                            "&:hover": {
                                borderColor: "#8095B7",
                            },
                        }}
                    >
                        Clear Filters
                    </Button>
                </SearchActionsRow>
            </SearchFormWrapper>
        </ClipFormContainer>
    );
};

export default SearchPanel;